/**
 * The unattended-screen self-heal (TIZEN-15, point 4).
 *
 * A television in a hotel corridor has nobody to press F5. When the render
 * tree dies, the choice is between a screen that stays broken until somebody
 * notices it and a screen that reloads itself — and the only reason not to do
 * the second is the failure mode it creates: a persistent fault turning into a
 * tight reload loop that hammers the API and never recovers either.
 *
 * `nenda-autoplayer` already solved this in `ErrorView.tsx` and has run it on
 * these panels for years. THE CONSTANTS BELOW ARE ITS SCARS, NOT PREFERENCES —
 * they are ported deliberately unchanged:
 *
 *   - 60 s base, so a transient fault costs a minute, not an hour.
 *   - ×2 per recent reload, so a persistent one backs away instead of looping.
 *   - 30 min ceiling, so a screen is never abandoned forever.
 *   - The counter resets after 10 minutes of uptime, because a screen that
 *     stayed up that long has effectively recovered and the next fault deserves
 *     a fresh 60 s rather than inheriting yesterday's backoff.
 *   - The log flush races a 3 s timeout, so the evidence usually escapes but a
 *     slow network can never delay the recovery it is evidence of.
 *
 * The state lives in `localStorage` because it has to survive the very reload
 * it is counting. That also means a device whose storage is wiped restarts the
 * backoff, which is the safe direction: it heals sooner and only risks doing so
 * more often.
 *
 * Deliberately platform-agnostic. The ticket scopes it to Tizen because that is
 * where we cannot reclaim the foreground, but an Android box gains exactly as
 * much from it and the code has no business knowing the difference.
 */

const BASE_MS = 60_000;
const MAX_MS = 30 * 60_000;
const RESET_MS = 10 * 60_000;
const FLUSH_RACE_MS = 3_000;
const STATE_KEY = "foyer.selfHeal";

interface HealState {
  count: number;
  at: number;
}

/**
 * How many reloads have happened RECENTLY. An older one does not count: a
 * screen that has been up past the reset window has recovered, and making the
 * next fault wait on that history would strand it for half an hour.
 */
export function recentReloadCount(now: number = Date.now()): number {
  try {
    const raw = window.localStorage.getItem(STATE_KEY);
    if (!raw) return 0;
    const parsed = JSON.parse(raw) as Partial<HealState>;
    if (now - (parsed.at ?? 0) > RESET_MS) return 0;
    const n = parsed.count;
    return typeof n === "number" && Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    // Unreadable state is the same as no state: heal sooner rather than later.
    return 0;
  }
}

/** How long to wait before the nth reload. Exported for the diagnostics view. */
export function healDelayMs(count: number): number {
  return Math.min(MAX_MS, BASE_MS * 2 ** Math.max(0, count));
}

/**
 * Arm a self-heal. Returns a cancel function, so a boundary that recovers on
 * its own (a retry that worked, a route change) can call off the reload.
 *
 * `flush` is given the chance to get the evidence out first, but only behind
 * the race above — a diagnostic upload must never be the reason a broken
 * television stays broken.
 */
export function armSelfHeal(
  reason: string,
  flush?: (message: string) => Promise<unknown>
): () => void {
  if (typeof window === "undefined") return () => {};
  const count = recentReloadCount();
  const delay = healDelayMs(count);
  console.warn(
    `[self-heal] armed: reloading in ${Math.round(delay / 1000)}s (attempt ${count + 1}) after ${reason}`
  );

  const timer = window.setTimeout(() => {
    void (async () => {
      // Record BEFORE reloading: the write has to survive the teardown it is
      // about to trigger, or the backoff resets every time and we are back to
      // the tight loop this exists to prevent.
      try {
        const next: HealState = { count: count + 1, at: Date.now() };
        window.localStorage.setItem(STATE_KEY, JSON.stringify(next));
      } catch {
        // Storage refused. Reload anyway - an un-counted recovery is far better
        // than a screen left broken because we could not take a note.
      }
      const message = `[self-heal] reloading (attempt ${count + 1}) after ${reason}`;
      if (flush) {
        await Promise.race([
          flush(message).catch(() => {}),
          new Promise((r) => window.setTimeout(r, FLUSH_RACE_MS)),
        ]).catch(() => {});
      }
      try {
        window.location.reload();
      } catch {
        /* nothing further we can do from here */
      }
    })();
  }, delay);

  return () => window.clearTimeout(timer);
}

/**
 * Declare the app healthy. Called once the TV has been up past the reset
 * window, so the NEXT fault starts from a 60 s delay rather than inheriting a
 * backoff earned hours ago.
 *
 * Clearing on a timer rather than at boot is the point: clearing at boot would
 * make every reload look like the first one, which is exactly how a crash loop
 * hides.
 */
export function markHealthy(): void {
  try {
    window.localStorage.removeItem(STATE_KEY);
  } catch {
    /* nothing to do */
  }
}

/** The uptime after which a screen counts as recovered. */
export const HEALTHY_AFTER_MS = RESET_MS;
